import { LocalDate, ZoneId } from '@js-joda/core';
import type { Interval } from '@js-joda/extra';
import '@js-joda/timezone';
import { QuantClient } from '@thinkalpha/quant-ws-client/client.js';
import type { InstitutionalHoldersQuery } from '@thinkalpha/quant-ws-client/client.js';
import { AnalystRatingsResponse } from '@thinkalpha/quant-ws-client/contracts/analyst-ratings.js';
import type { AnalystRatingsQuery } from '@thinkalpha/quant-ws-client/contracts/analyst-ratings.js';
import { InstitutionalHoldersResponse } from '@thinkalpha/quant-ws-client/contracts/institutional-holders.js';
import type { InstitutionalHoldersResponse as InstitutionalHoldersResponseType } from '@thinkalpha/quant-ws-client/contracts/institutional-holders.js';
import type { Logo } from '@thinkalpha/quant-ws-client/contracts/logo.js';
import { DaysStatus } from '@thinkalpha/quant-ws-client/contracts/market.js';
import type { SpecificTradingHours } from '@thinkalpha/quant-ws-client/contracts/market.js';
import type { NewsQuery, NewsStory, FullNewsStory } from '@thinkalpha/quant-ws-client/contracts/news.js';
import { createInstance } from 'src/api';
import { appConfig } from 'src/lib/config';

const axios = createInstance({ baseURL: appConfig.newQuantApi });
const zodAxios = createInstance({ baseURL: appConfig.newQuantApi }, { useLegacySerialization: false });
export const quantWsClient = new QuantClient(zodAxios);
if (typeof window !== 'undefined') (window as any).quantClient = quantWsClient; // Expose for debugging purposes

export async function getBulkNewsBySymbol(payload: NewsQuery): Promise<NewsStory[]> {
    // If symbols is defined and empty, return an empty array
    // This distinguishes between "all symbols" (undefined) and "no symbols" (empty array)
    if (payload.symbols !== undefined && payload.symbols.length === 0) {
        return [];
    }

    const resp = await axios.post('/news/queries', payload);

    // Sort news items in reverse chronological order (newest first), then by ID for consistency.
    // Note: `sort` payload parameter is only used to determine where the results are fetched relative to `start`/'end`
    return (resp.data as NewsStory[]).sort((a, b) => {
        const timeComparison = b.publishedAt.compareTo(a.publishedAt);

        if (timeComparison !== 0) return timeComparison;

        return b.id.localeCompare(a.id);
    });
}

export async function getFullArticleContent(articleId: string): Promise<FullNewsStory> {
    const resp = await axios.get(`/news/story/${articleId}`);
    return resp.data;
}

export function getCompanyLogoFromSymbol(symbol: string): Promise<Logo> {
    return axios.get(`/logos/by-symbol/${encodeURIComponent(symbol)}`).then((res) => res.data);
}

export async function getAnalystRatings(payload: AnalystRatingsQuery): Promise<AnalystRatingsResponse> {
    // zodAxios runs with `useLegacySerialization: false` so InstantCodec /
    // LocalDateCodec round-trip cleanly through the request body and the
    // schema parse on the response.
    const resp = await zodAxios.post<unknown>('/analyst-ratings/queries', payload);
    return AnalystRatingsResponse.parse(resp.data);
}

export function getInstitutionalHolders(
    symbol: string,
    query?: InstitutionalHoldersQuery,
): Promise<InstitutionalHoldersResponseType> {
    const { asOfDate, ...params } = query ?? {};
    return zodAxios
        .get<unknown>(`/symbols/${encodeURIComponent(symbol)}/institutional-holders`, {
            params: {
                ...params,
                ...(asOfDate !== undefined ? { asOfDate: asOfDate.toString() } : {}),
            },
            validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
        })
        .then((response) => {
            if (response.status !== 404) return InstitutionalHoldersResponse.parse(response.data);
            const currentDate = LocalDate.now(ZoneId.of('America/New_York'));
            return {
                id: `${symbol}:no-institutional-holders`,
                symbol,
                asOfDate: asOfDate ?? currentDate,
                currentDate,
                diffStartDate: currentDate,
                kpis: {
                    holdingsCount: 0,
                    totalPctOutstanding: 0,
                    countNew: 0,
                    netChange: 0,
                    countClosed: 0,
                    hasDiff: false,
                },
                holders: [],
                totalHolders: 0,
                filteredHolders: 0,
                returnedHolders: 0,
                skip: params.skip ?? 0,
                limit: params.limit ?? 0,
            } satisfies InstitutionalHoldersResponseType;
        });
}

export async function getTradingHoursForMicCode(mic: string, timeframe: Interval): Promise<SpecificTradingHours> {
    return quantWsClient.getTradingHoursForMarket(mic, timeframe);
}

export async function getTimezoneForMicCode(mic: string): Promise<ZoneId> {
    return quantWsClient.getTimezoneOfMarket(mic);
}

export async function getDaysStatusForMicCode(
    mic: string,
    timeframe: Interval,
    mode: 'all' | 'only-open' | 'only-closed' | 'only-holidays',
): Promise<DaysStatus> {
    const response = await zodAxios.get<unknown>(`/markets/${mic}/days-status`, {
        params: {
            start: timeframe.start().toString(),
            end: timeframe.end().toString(),
            mode,
        },
    });

    // Parse and validate with zod schema (automatically transforms ISO strings to LocalDate)
    const parsed = DaysStatus.parse(response.data);
    return parsed;
}
